// ── Overview Tab — KPIs + Roteiro ─────────────────────────────────────────────
import { getTrip }               from '@/store/tripState';
import { fmt, toArr, fmtR,
         diffDays, sanitize }   from '@/helpers';
import { AGENDA_CAT_ICONS }      from '@/constants';
import type { AgendaItem,
              Transporte, Hotel } from '@/types';

export function renderSummary(): void {
  renderKPIs();
}

// ── KPI cards ─────────────────────────────────────────────────
function renderKPIs(): void {
  const el = document.getElementById('sum-grid');
  if (!el) return;

  const trip      = getTrip();
  const today     = new Date().toISOString().slice(0, 10);
  const travelers = toArr(trip.travelers ?? {});
  const transArr  = toArr(trip.transporte ?? {}) as Transporte[];
  const hotelArr  = toArr(trip.hoteis     ?? {}) as Hotel[];
  const agendaArr = toArr(trip.agenda     ?? {}) as AgendaItem[];
  const checks    = toArr(trip.checks     ?? {});
  const destArr   = toArr(trip.destinos   ?? {});
  const expenses  = toArr(trip.expenses   ?? {});

  const doneChecks  = checks.filter(c => c.done).length;
  const doneAgenda  = agendaArr.filter(a => a.done || a.status === 'feito').length;
  const totalSpend  = [...transArr, ...hotelArr]
    .reduce((s, i) => s + ((i as {preco?: number}).preco ?? 0), 0) +
    expenses.filter(e => (e as {currency?: string}).currency === 'BRL')
      .reduce((s, e) => s + ((e as {amount?: number}).amount ?? 0), 0);

  const daysUntil = trip.start ? diffDays(today, trip.start) : -1;
  const tripStarted = trip.start ? today >= trip.start : false;
  const tripEnded   = trip.end   ? today >  trip.end   : false;

  const cards: { icon: string; label: string; value: string | number; sub: string; highlight?: boolean }[] = [];

  // Countdown — only show if trip hasn't started
  if (daysUntil > 0) {
    cards.push({
      icon: '⏳', label: 'Faltam', value: `${daysUntil}d`,
      sub: `para a viagem começar em ${fmt(trip.start ?? '')}`, highlight: true,
    });
  } else if (tripStarted && !tripEnded) {
    cards.push({
      icon: '🎉', label: 'Em viagem!', value: '✈️',
      sub: `termina em ${fmt(trip.end ?? '')}`, highlight: true,
    });
  }

  cards.push(
    { icon: '🌍', label: 'Destinos',    value: destArr.length,   sub: destArr.map((d: {cidade: string}) => sanitize(d.cidade)).join(', ') || 'Nenhum ainda' },
    { icon: '👥', label: 'Viajantes',   value: travelers.length, sub: travelers.map((t: {name: string}) => sanitize(t.name)).join(', ') || 'Nenhum ainda' },
    { icon: '📅', label: 'Dias',        value: diffDays(trip.start ?? '', trip.end ?? '') || '—', sub: `${fmt(trip.start ?? '')} → ${fmt(trip.end ?? '')}` },
    { icon: '✈️', label: 'Transportes', value: transArr.length,  sub: `${transArr.filter(t => t.status === 'confirmado').length} confirmados` },
    { icon: '🏨', label: 'Hotéis',      value: hotelArr.length,  sub: `${hotelArr.filter(h => h.status === 'confirmado').length} confirmados` },
    { icon: '🎯', label: 'Agenda',      value: `${doneAgenda}/${agendaArr.length}`, sub: 'eventos concluídos' },
    { icon: '✅', label: 'Checklist',   value: `${doneChecks}/${checks.length}`, sub: checks.length > 0 && doneChecks === checks.length ? 'Tudo pronto! 🎉' : 'itens prontos' },
    { icon: '💰', label: 'Gasto (BRL)', value: fmtR(totalSpend), sub: trip.budgetLimit ? `de ${fmtR(trip.budgetLimit)}` : 'estimado' },
  );

  el.innerHTML = cards.map(({ icon, label, value, sub, highlight }) => `
    <div class="sum-card${highlight ? ' sum-card-highlight' : ''}">
      <div class="sum-card-icon">${icon}</div>
      <div class="sum-card-val">${value}</div>
      <div class="sum-card-label">${label}</div>
      <div class="sum-card-sub" title="${sanitize(sub)}">${sub}</div>
    </div>`).join('');
}

// ── Roteiro = timeline da viagem ──────────────────────────────
export function renderTimeline(): void {
  const el = document.getElementById('timeline');
  if (!el) return;

  const trip  = getTrip();
  const today = new Date().toISOString().slice(0, 10);

  type TLEvent = {
    date: string; time?: string; label: string;
    sub: string; color: string; icon: string; isPast: boolean;
  };
  const events: TLEvent[] = [];

  (toArr(trip.transporte ?? {}) as Transporte[]).forEach(t => {
    if (!t.data) return;
    events.push({ date: t.data, time: t.horaPartida || t.hora,
      label: `${t.tipo ?? 'Transporte'}: ${sanitize(t.origem ?? '')}${t.destino ? ' → ' + sanitize(t.destino) : ''}`,
      sub: [
        t.dataChegada || t.horaChegada
          ? `Chegada ${[t.dataChegada ? fmt(t.dataChegada) : '', t.horaChegada ? sanitize(t.horaChegada) : ''].filter(Boolean).join(' às ')}`
          : '',
        [t.operadora, t.num].filter(Boolean).map(s => sanitize(s as string)).join(' '),
      ].filter(Boolean).join(' · '),
      color: '#00A3E0', icon: '✈️', isPast: t.data < today });
  });

  (toArr(trip.hoteis ?? {}) as Hotel[]).forEach(h => {
    if (h.checkin) events.push({ date: h.checkin, time: h.checkinTime,
      label: `Check-in: ${sanitize(h.nome ?? 'Hotel')}`, sub: sanitize(h.cidade ?? ''),
      color: '#1E9E5E', icon: '🏨', isPast: h.checkin < today });
    if (h.checkout) events.push({ date: h.checkout, time: h.checkoutTime,
      label: `Check-out: ${sanitize(h.nome ?? 'Hotel')}`, sub: `${h.noites ?? 0} noites`,
      color: '#6D5FD5', icon: '🏁', isPast: h.checkout < today });
  });

  (toArr(trip.agenda ?? {}) as AgendaItem[])
    .filter(a => a.status === 'confirmado' || (!a.status && !a.done))
    .forEach(a => {
      if (!a.date) return;
      events.push({ date: a.date, time: a.time, label: sanitize(a.title ?? ''),
        sub: sanitize(a.local ?? ''), color: '#D97706',
        icon: AGENDA_CAT_ICONS[a.cat ?? ''] ?? '📌',
        isPast: a.done || a.date < today });
    });

  events.sort((a, b) => a.date.localeCompare(b.date) || (a.time ?? '').localeCompare(b.time ?? ''));

  if (!events.length) {
    el.innerHTML = '<div class="empty">Adicione transportes, hotéis e itens de agenda para ver o roteiro aqui.</div>';
    return;
  }

  const byDate: Record<string, TLEvent[]> = {};
  events.forEach(e => { (byDate[e.date] ??= []).push(e); });

  el.innerHTML = Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, evts]) => {
      const isToday = date === today;
      return `
        <div class="tl-day${isToday ? ' tl-day-today' : ''}">
          <div class="tl-day-header">
            <div class="tl-day-date">${fmt(date)}</div>
            ${isToday ? '<span class="badge b-ok" style="font-size:10px">Hoje</span>' : ''}
          </div>
          <div class="tl-day-events">
            ${evts.map(e => `
              <div class="tl-event${e.isPast ? ' tl-event-past' : ''}">
                <div class="tl-event-dot" style="background:${e.isPast ? 'var(--border2)' : e.color}">
                  <span style="font-size:10px">${e.icon}</span>
                </div>
                <div class="tl-event-body">
                  <div class="tl-event-label">${e.label}</div>
                  ${e.sub || e.time ? `<div class="tl-event-sub">${[e.time, e.sub].filter(Boolean).join(' · ')}</div>` : ''}
                </div>
              </div>`).join('')}
          </div>
        </div>`;
    }).join('');
}
